import React, { useEffect, useMemo, useState } from "react";
import TableHead from "@/components/tables/TableHead";
import TemplateTable from "@/components/tables/TemplateTable";
import TransferOwnershipButton from "@/components/buttons/TransferOwnershipButton";
import TransferOwnershipModal from "@/components/modals/TransferOwnershipModal";
import {
    fetchTemplates,
    transferBulkTemplateOwnership,
} from "@/services/template.service";
import { useDataQueries, normalizeSearchItem } from "@/utils/hooks/useDataQueries";
import { useFeaturePermission } from "@/utils/hooks/useFeaturePermission";
import {
    buildTransferOwnershipPayload,
    useOwnershipSelection,
} from "@/utils/hooks/useOwnershipSelection";
import { useCompactListLayout } from "@/utils/hooks/useCompactListLayout";
import {
    getListCacheBootstrap,
    invalidateOwnershipTransferListCaches,
    writeListCache,
} from "@/utils/listPageCache";
import { GLOBAL_ORGANIZATION, LABELS } from "@/utils/constants/appConstants/commonConstants";
import {
    BULK_TRANSFER_OWNERSHIP_QUEUED,
    BULK_TRANSFER_OWNERSHIP_SUCCESS,
} from "@/utils/constants/messages";
import { FEATURE_PERMISSIONS } from "@/utils/constants/featurePermissions";
import { getCurrentOrganization } from "@/utils/functions/OrganizationFunctions";
import { errorToasterMessage, successToasterMessage } from "@/utils/functions/toaster";

type Props = {};

function TemplateList({}: Props) {
    const [
        pageNo,
        setPageNo,
        dataLength,
        setDataLength,
        sorting,
        setSorting,
        filter,
        setFilter,
        searchItem,
        setSearchItem,
        resetDataQueries,
    ] = useDataQueries();
    const isCompactLayout = useCompactListLayout();
    const selection = useOwnershipSelection();
    const [showBulkTransferModal, setShowBulkTransferModal] = useState(false);
    const [bulkTransferLoading, setBulkTransferLoading] = useState(false);
    const isTransferTemplateOwnershipPermitted = useFeaturePermission(FEATURE_PERMISSIONS.AdminPortalTransferTemplateOwnership);
    const showTransferActions =
        isTransferTemplateOwnershipPermitted &&
        getCurrentOrganization()?.name !== GLOBAL_ORGANIZATION;

    const listBootstrap = getListCacheBootstrap("templates", {
        dataLength,
        sorting,
        searchItem,
        filter,
    });

    const [allFetched, setAllFetched] = useState(false);
    const [loadData, setLoadData] = useState(!listBootstrap.hasCache);
    const [totalCount, setTotalCount] = useState(
        listBootstrap.totalCount
    );
    const [isLoading, setIsLoading] = useState(
        !listBootstrap.hasCache
    );
    const [templates, setTemplates] = useState<any[]>(
        listBootstrap.items
    );
    const [templatesLeft, setTemplatesLeft] = useState(true);
    const [templateHistory, setTemplateHistory] = useState<any[]>(
        listBootstrap.items
    );

    /**
     * Fetch templates.
     *
     * page === 1:
     * - replaces existing data
     *
     * page > 1:
     * - appends data for infinite scroll
     */
    const fetchTemplateList = (
        page = 1,
        currentFilter = filter,
        currentSearch = searchItem
    ) => {
        const isInitialLoad = page === 1;
        const normalizedSearch = normalizeSearchItem(currentSearch);
        const { cacheKey, cached } = getListCacheBootstrap("templates", {
            dataLength,
            sorting,
            searchItem: normalizedSearch,
            filter: currentFilter,
        });

        if (isInitialLoad && cached) {
            setTemplates(cached.items as any[]);
            setTemplateHistory(cached.items as any[]);
            setTotalCount(cached.totalCount);
            setLoadData(false);
            setIsLoading(false);
            setTemplatesLeft((cached.items as any[]).length >= dataLength);
            setAllFetched((cached.items as any[]).length < dataLength);
            return;
        }

        if (isInitialLoad) {
            setLoadData(true);
        }
        setIsLoading(true);

        fetchTemplates(
            page,
            dataLength,
            sorting,
            currentFilter,
            normalizedSearch,
            (response: any) => {
                const nextTemplates =
                    response?.data?.templates ?? [];

                const total =
                    response?.data?.total_count ?? 0;

                setTotalCount(total);

                if (page === 1) {
                    writeListCache(cacheKey, nextTemplates, total);
                    setTemplates(nextTemplates);
                    setTemplateHistory(nextTemplates);
                } else {
                    setTemplates((prev) => [
                        ...prev,
                        ...nextTemplates,
                    ]);

                    setTemplateHistory((prev) => [
                        ...prev,
                        ...nextTemplates,
                    ]);
                }

                if (nextTemplates.length < dataLength) {
                    setTemplatesLeft(false);
                    setAllFetched(true);
                } else {
                    setTemplatesLeft(true);
                    setAllFetched(false);
                }

                setLoadData(false);
                setIsLoading(false);
            },
            (error: any) => {
                console.error(error);

                setLoadData(false);
                setIsLoading(false);
            }
        );
    };

    /**
     * Initial fetch.
     *
     * Only runs when the page configuration changes.
     *
     * IMPORTANT:
     * Do NOT add `filter` here.
     *
     * Filter changes are handled explicitly by Apply/Reset.
     */
    useEffect(() => {
        if (listBootstrap.hasCache) {
            return;
        }

        setPageNo(1);
        setTemplates([]);
        setTemplateHistory([]);
        setTemplatesLeft(true);
        setAllFetched(false);

        fetchTemplateList(1, filter, searchItem);
    }, []);

    /**
     * Handle infinite scroll.
     */
    useEffect(() => {
        const handleScroll = () => {
            if (
                allFetched ||
                isLoading ||
                !templatesLeft
            ) {
                return;
            }

            const reachedBottom =
                window.innerHeight +
                    document.documentElement.scrollTop >=
                document.documentElement.offsetHeight - 1;

            if (!reachedBottom) {
                return;
            }

            const nextPage = Number(pageNo) + 1;

            setPageNo(nextPage);

            fetchTemplateList(
                nextPage,
                filter,
                searchItem
            );
        };

        window.addEventListener(
            "scroll",
            handleScroll
        );

        return () => {
            window.removeEventListener(
                "scroll",
                handleScroll
            );
        };
    }, [
        pageNo,
        dataLength,
        sorting,
        filter,
        searchItem,
        allFetched,
        isLoading,
        templatesLeft,
    ]);

    /**
     * Search.
     */
    const search = (searchText: string) => {
        const nextSearch = normalizeSearchItem(searchText);
        setSearchItem(nextSearch);

        setPageNo(1);
        setTemplatesLeft(true);
        setAllFetched(false);

        fetchTemplateList(1, filter, nextSearch);
    };

    /**
     * Reset filters/search and fetch all templates.
     */
    const resetResponse = () => {
        const emptyFilter = {};

        setPageNo(1);
        setTemplates([]);
        setTemplateHistory([]);
        setTemplatesLeft(true);
        setAllFetched(false);
        setTotalCount(0);

        setLoadData(true);
        setIsLoading(true);

        setFilter(emptyFilter);
        setSearchItem(null);
        selection.clear();

        fetchTemplateList(
            1,
            emptyFilter,
            null
        );
    };

    /**
     * Full reset.
     */
    const reset = () => {
        resetDataQueries?.();
        resetResponse();
    };

    /**
     * Apply filters.
     *
     * API is called ONLY here when the user clicks Apply.
     */
    const handleApplyFilter = (
        filterOverride?: any
    ) => {
        const activeFilter =
            filterOverride ?? filter;

        setPageNo(1);
        setTemplates([]);
        setTemplateHistory([]);
        setTemplatesLeft(true);
        setAllFetched(false);

        setLoadData(true);
        setIsLoading(true);

        setFilter(activeFilter);

        fetchTemplateList(
            1,
            activeFilter,
            searchItem
        );
    };

    const templateIds = templates.map((template) => template.id).filter(Boolean);

    const reloadTemplates = () => {
        invalidateOwnershipTransferListCaches("template");
        setPageNo(1);
        setTemplates([]);
        setTemplateHistory([]);
        setTemplatesLeft(true);
        setAllFetched(false);
        setLoadData(true);
        setIsLoading(true);
        fetchTemplateList(1, filter, searchItem);
    };

    const handleBulkTransferOwnership = async (selectedUser: any) => {
        if (!selectedUser || selection.selectedIds.length === 0) return;

        setBulkTransferLoading(true);
        try {
            const payload = buildTransferOwnershipPayload(selectedUser);
            const result = await transferBulkTemplateOwnership({
                template_ids: selection.selectedIds,
                ...payload,
            });
            selection.clear();
            setShowBulkTransferModal(false);
            successToasterMessage(
                result?.is_async
                    ? BULK_TRANSFER_OWNERSHIP_QUEUED
                    : BULK_TRANSFER_OWNERSHIP_SUCCESS,
            );
            reloadTemplates();
        } catch (error) {
            console.log("Error bulk transferring template ownership:", error);
            errorToasterMessage(error);
        } finally {
            setBulkTransferLoading(false);
        }
    };

    return (
        <div className="gs-list-page-card">
            <div className="gs-list-page-card-header">
                <TableHead
                    id="template-table"
                    handleReset={resetResponse}
                    pageNo={pageNo as number}
                    dataLength={dataLength as number}
                    setFilter={setFilter}
                    filter={filter}
                    setDataLength={
                        setDataLength as (
                            dataLength: number
                        ) => void
                    }
                    reset={reset}
                    search={search}
                    title="Templates"
                    currentEnvo={templates.length}
                    totalEnvo={totalCount}
                    handleApplyFilter={
                        handleApplyFilter
                    }
                    searchItem={searchItem as string}
                    selectedCount={showTransferActions ? selection.selectedIds.length : 0}
                    showMultiselectSelectAll={showTransferActions && isCompactLayout && templateIds.length > 0}
                    selectAllChecked={
                        selection.areAllSelected(templateIds)
                            ? true
                            : selection.areSomeSelected(templateIds)
                              ? "indeterminate"
                              : false
                    }
                    onSelectAllChange={() => selection.toggleAll(templateIds)}
                    multiselectAction={
                        showTransferActions ? (
                            <TransferOwnershipButton
                                id="template-table-bulk-transfer-btn"
                                iconId="template-table-bulk-transfer-icon"
                                className="gs-transfer-ownership-btn--toolbar"
                                onClick={() => setShowBulkTransferModal(true)}
                            />
                        ) : null
                    }
                />
            </div>

            <TemplateTable
                id="template-table"
                reset={reset}
                title="Templates"
                setTemplates={setTemplates}
                search={search}
                isLoading={isLoading}
                dataLength={dataLength as number}
                sorting={sorting}
                setSorting={
                    setSorting as (
                        sorting: any
                    ) => void
                }
                searchItem={
                    searchItem as string
                }
                allFetched={allFetched}
                templates={templates}
                dataLoad={loadData}
                resetDataQueries={reloadTemplates}
                setPageNo={setPageNo}
                loadingData={isLoading}
                selection={selection}
                showTransferActions={showTransferActions}
            />
            {showBulkTransferModal ? (
                <TransferOwnershipModal
                    show={showBulkTransferModal}
                    onHide={() => setShowBulkTransferModal(false)}
                    onTransferOwnership={handleBulkTransferOwnership}
                    loading={bulkTransferLoading}
                    title={LABELS.TRANSFER_TEMPLATE_OWNERSHIP}
                    description={LABELS.BULK_TRANSFER_TEMPLATE_OWNERSHIP_DESCRIPTION}
                />
            ) : null}
        </div>
    );
}

export default TemplateList;
